import React, { useCallback, useMemo, useState } from 'react';
import {
  applyEditToRow,
  PricingAccountRow,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { BookmarkNotePopover } from '@sales-portal/domain/pricing/components/bookmark-note-popover';
import {
  buildPricingEdit,
  computeActionEdit,
  getLastInvoiceGrossMarginPercentage,
  getLastInvoicePriceInMicros,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/quick-action-calculations';
import {
  buildPricingInputValidator,
  validatePricingFloor,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/pricing-input-validator';
import { createStyles } from '@/contexts/theme-context';
import {
  EditScope,
  GrossMarginEditAction,
  PriceEditAction,
  PricingEdit,
  PricingEditField,
  RevertEditAction,
} from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { EMPTY_VALUE_LABEL } from '@sales-portal/constants';
import {
  formatCurrency,
  formatPriceInMicrosWithoutSymbol,
  MICRO_CONVERSION,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import { formatPriceForSave } from '@/utils/price-editing';
import { getPriceByCostAndMargin } from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import {
  getQuickActionMetadata,
  PricingTableQuickAction,
} from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-quick-actions-popover';
import { isEditAtOriginalBaseline } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/utils/pricing-edit-normalization';
import {
  isValidGrossMarginPercentage,
  isValidPriceInMicros,
} from '@/utils/pricing-validation';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingCalculations } from '@/utils/pricing-calculations';
import {
  PricingTableInput,
  PricingTableInputPopoverConfig,
  PricingTableInputPopoverKind,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-input';
import {
  selectPriceEdit,
  usePricingEditsStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBookmarkAction } from '@sales-portal/domain/pricing/blocs/hooks/use-bookmark-action';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useInputArrowNavigation } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-input-arrow-navigation';
import { usePricingCellFocus } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-focus';
import { usePricingTableUIStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-table-ui-store';
import { useUpdatedPricingRow } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-updated-pricing-row';
import { VariantPackCostType } from '@/types';
import { View } from 'react-native';

interface Props {
  canBookmarkItem: boolean;
  /** Table column id, for the table's horizontal scroll-to-focus. */
  columnId: string;
  currencyCode: string;
  disabled: boolean;
  inputType: PricingEditField;
  isHistoricalPricingContextEnabled: boolean;
  originalFormattedValue: string;
  originalRow: PricingAccountRow;
  pageSource?: PricingPageSource;
  priceFloorEnabled: boolean;
  priceFloorMode?: VariantPackCostType | null;
  restaurantUUID: string;
  rowIndex: string;
  scope: EditScope;
  /** Positional index in the rendered rows, for the table's scroll-to-focus. */
  tableRowIndex: number;
  value: string;
  variantPackUUID: string;
}

export const ItemInputCell: React.FC<Props> = ({
  canBookmarkItem,
  columnId,
  currencyCode,
  disabled,
  inputType,
  isHistoricalPricingContextEnabled,
  originalFormattedValue,
  originalRow,
  pageSource = PricingPageSource.ITEM_DETAILS,
  priceFloorEnabled,
  priceFloorMode,
  restaurantUUID,
  rowIndex,
  scope,
  tableRowIndex,
  value,
  variantPackUUID,
}) => {
  const [styles, theme] = useStyles();
  const { showToast } = useExperienceToasts();
  const applyEdits = usePricingEditsStore((store) => store.applyEdits);
  const revertEdits = usePricingEditsStore(
    (store) => store.revertEditsByVariantPack,
  );
  // The flag is table-wide, but only the focused cell's popover can show it;
  // gating the selector keeps every other cell out of the re-render.
  const [isFocused, setIsFocused] = useState(false);
  const isQuickActionsOpen = usePricingTableUIStore(
    (store) => isFocused && store.isQuickActionsOpen,
  );
  const setQuickActionsOpen = usePricingTableUIStore(
    (store) => store.setQuickActionsOpen,
  );
  const { hasChanges, updatedRow } = useUpdatedPricingRow(
    variantPackUUID,
    restaurantUUID,
    originalRow,
  );

  const { bookmarkAction, bookmarkNote, isBookmarked, isNoteOpen, onSaveNote } =
    useBookmarkAction({
      canBookmarkItem,
      pageSource,
      restaurantUUID,
      supplierUUID: originalRow.supplierUUID,
      variantPackUUID,
    });
  const inputID = `pricing-input-${inputType}-${rowIndex}`;
  const onArrowNavigation = useInputArrowNavigation(
    inputID,
    `pricing-input-${inputType}-`,
  );
  const { isAnchorSettled, onFocus: reportCellFocus } = usePricingCellFocus(
    tableRowIndex,
    columnId,
  );

  const applyComputedEdit = useCallback(
    (edit: PricingEdit | null) => {
      if (!edit) {
        return;
      }

      if (priceFloorEnabled && Boolean(priceFloorMode)) {
        const validation = validatePricingFloor({
          costInMicros: originalRow.costInMicros,
          currencyCode,
          grossMarginPercentage: edit.grossMarginPercentage,
          inputType,
          priceInMicros: edit.priceInMicros,
        });
        if (!validation.valid) {
          showToast({ title: validation.message, variant: 'error' });
          return;
        }
      }

      applyEdits([edit], scope);
    },
    [
      applyEdits,
      currencyCode,
      inputType,
      originalRow.costInMicros,
      priceFloorEnabled,
      priceFloorMode,
      scope,
      showToast,
    ],
  );

  // The price input drops the currency symbol so the number gets the whole
  // field; the symbol still shows on the original value below it.
  const inputValue = useMemo(
    () =>
      inputType === PricingEditField.Price
        ? formatPriceInMicrosWithoutSymbol(
            originalRow.priceInMicros,
            currencyCode,
          )
        : value,
    [currencyCode, inputType, originalRow.priceInMicros, value],
  );

  const updatedValue = useMemo(() => {
    if (!hasChanges) {
      return null;
    }
    if (inputType === PricingEditField.Price) {
      return formatPriceInMicrosWithoutSymbol(updatedRow.priceInMicros);
    }
    if (inputType === PricingEditField.GrossMarginPercentage) {
      return updatedRow.grossMarginPercentage.toFixed(1);
    }
    return null;
  }, [
    hasChanges,
    inputType,
    updatedRow.grossMarginPercentage,
    updatedRow.priceInMicros,
  ]);

  const onBlur = useCallback(
    (value: string) => {
      const priceInMicros =
        inputType === PricingEditField.Price
          ? formatPriceForSave(Number(value) * MICRO_CONVERSION)
          : getPriceByCostAndMargin(originalRow, Number(value));

      if (!isValidPriceInMicros(priceInMicros)) {
        return;
      }

      const grossMarginPercentage =
        inputType === PricingEditField.Price
          ? PricingCalculations.grossMarginPercentFromGrossProfit(
              PricingCalculations.grossProfitAmountFromPrice(
                priceInMicros,
                originalRow.costInMicros,
              ),
              priceInMicros,
            )
          : Number(value);

      if (!isValidGrossMarginPercentage(grossMarginPercentage)) {
        return;
      }

      if (
        isEditAtOriginalBaseline({
          edit: {
            grossMarginPercentage,
            priceInMicros,
            updatedField: inputType,
          },
          originalRow,
        })
      ) {
        revertEdits(variantPackUUID, [restaurantUUID], scope);
        return;
      }

      const edit = buildPricingEdit({
        grossMarginPercentage,
        originalRow,
        priceInMicros,
        restaurantUUID,
        updatedField: inputType,
        variantPackUUID,
      });

      if (edit) {
        applyEdits([edit], scope);
        pricingManagementAnalytics.track(PricingAnalyticEvent.INLINE_EDIT, {
          pageSource,
          quickAction: null,
          restaurantUUID,
          supplierUUID: originalRow.supplierUUID,
          variantPackUUID,
        });
      }
    },
    [
      applyEdits,
      inputType,
      originalRow,
      pageSource,
      restaurantUUID,
      revertEdits,
      scope,
      variantPackUUID,
    ],
  );

  const quickActions = useMemo<PricingTableQuickAction[]>(() => {
    const actionComputationInput = {
      effectiveRow: updatedRow,
      originalRow,
      restaurantUUID,
      variantPackUUID,
    };

    const applyFreshEdit = (
      type: GrossMarginEditAction | PriceEditAction,
      previewedEdit: PricingEdit | null,
    ) => {
      const currentEdit = selectPriceEdit(
        usePricingEditsStore.getState(),
        variantPackUUID,
        restaurantUUID,
      );
      const effectiveRow = currentEdit
        ? applyEditToRow(originalRow, currentEdit)
        : originalRow;

      applyComputedEdit(
        effectiveRow.priceInMicros === updatedRow.priceInMicros
          ? previewedEdit
          : computeActionEdit({
              ...actionComputationInput,
              effectiveRow,
              type,
            }),
      );
    };

    const trackInlineEdit = (
      quickAction: GrossMarginEditAction | PriceEditAction,
    ) => {
      pricingManagementAnalytics.track(PricingAnalyticEvent.INLINE_EDIT, {
        pageSource,
        quickAction,
        restaurantUUID,
        supplierUUID: originalRow.supplierUUID,
        variantPackUUID,
      });
    };

    const revertAction: PricingTableQuickAction = {
      ...getQuickActionMetadata(RevertEditAction.RevertEdit),
      disabled: !hasChanges,
      onSelect: () => {
        revertEdits(variantPackUUID, [restaurantUUID], scope);
        pricingManagementAnalytics.track(
          PricingAnalyticEvent.INLINE_REVERSION,
          {
            pageSource,
            restaurantUUID,
            supplierUUID: originalRow.supplierUUID,
            variantPackUUID,
          },
        );
      },
      valueLabel: originalFormattedValue,
    };

    const gmValueLabel = (edit: PricingEdit | null) =>
      edit
        ? formatPercentage(edit.grossMarginPercentage, 1)
        : EMPTY_VALUE_LABEL;
    const priceValueLabel = (edit: PricingEdit | null) =>
      edit
        ? formatCurrency(edit.priceInMicros, currencyCode)
        : EMPTY_VALUE_LABEL;

    if (inputType === PricingEditField.GrossMarginPercentage) {
      const addEdit = computeActionEdit({
        ...actionComputationInput,
        type: GrossMarginEditAction.AddOnePercent,
      });
      const subtractEdit = computeActionEdit({
        ...actionComputationInput,
        type: GrossMarginEditAction.SubtractOnePercent,
      });
      const lastMarginEdit = computeActionEdit({
        ...actionComputationInput,
        type: GrossMarginEditAction.SetLastMargin,
      });
      const lastMargin = getLastInvoiceGrossMarginPercentage(
        originalRow.invoiceHistory,
      );
      const hasLastMargin = lastMargin !== null;

      return [
        {
          ...getQuickActionMetadata(GrossMarginEditAction.AddOnePercent),
          onSelect: () => {
            applyFreshEdit(GrossMarginEditAction.AddOnePercent, addEdit);
            trackInlineEdit(GrossMarginEditAction.AddOnePercent);
          },
          valueLabel: gmValueLabel(addEdit),
        },
        {
          ...getQuickActionMetadata(GrossMarginEditAction.SubtractOnePercent),
          onSelect: () => {
            applyFreshEdit(
              GrossMarginEditAction.SubtractOnePercent,
              subtractEdit,
            );
            trackInlineEdit(GrossMarginEditAction.SubtractOnePercent);
          },
          valueLabel: gmValueLabel(subtractEdit),
        },
        ...(hasLastMargin
          ? [
              {
                ...getQuickActionMetadata(GrossMarginEditAction.SetLastMargin),
                disabled: lastMarginEdit === null,
                onSelect: () => {
                  applyFreshEdit(
                    GrossMarginEditAction.SetLastMargin,
                    lastMarginEdit,
                  );
                  pricingManagementAnalytics.track(
                    PricingAnalyticEvent.INLINE_EDIT,
                    {
                      pageSource,
                      quickAction: GrossMarginEditAction.SetLastMargin,
                      restaurantUUID,
                      supplierUUID: originalRow.supplierUUID,
                      variantPackUUID,
                    },
                  );
                },
                valueLabel:
                  lastMargin !== null
                    ? formatPercentage(lastMargin, 1)
                    : EMPTY_VALUE_LABEL,
              },
            ]
          : []),
        ...(bookmarkAction ? [bookmarkAction] : []),
        revertAction,
      ];
    }

    const endInX9Edit = computeActionEdit({
      ...actionComputationInput,
      type: PriceEditAction.EndInX9,
    });
    const randomRoundUpEdit = computeActionEdit({
      ...actionComputationInput,
      type: PriceEditAction.RandomRoundUp,
    });
    const lastPriceEdit = computeActionEdit({
      ...actionComputationInput,
      type: PriceEditAction.SetLastPrice,
    });
    const lastPriceInMicros = getLastInvoicePriceInMicros(
      originalRow.invoiceHistory,
    );
    const hasLastPrice = lastPriceInMicros !== null;

    return [
      {
        ...getQuickActionMetadata(PriceEditAction.EndInX9),
        disabled: endInX9Edit === null,
        onSelect: () => {
          applyFreshEdit(PriceEditAction.EndInX9, endInX9Edit);
          trackInlineEdit(PriceEditAction.EndInX9);
        },
        valueLabel: priceValueLabel(endInX9Edit),
      },
      {
        ...getQuickActionMetadata(PriceEditAction.RandomRoundUp),
        disabled: randomRoundUpEdit === null,
        onSelect: () => {
          applyFreshEdit(PriceEditAction.RandomRoundUp, randomRoundUpEdit);
          trackInlineEdit(PriceEditAction.RandomRoundUp);
        },
        valueLabel: priceValueLabel(randomRoundUpEdit),
      },
      ...(hasLastPrice
        ? [
            {
              ...getQuickActionMetadata(PriceEditAction.SetLastPrice),
              disabled: lastPriceEdit === null,
              onSelect: () => {
                applyFreshEdit(PriceEditAction.SetLastPrice, lastPriceEdit);
                pricingManagementAnalytics.track(
                  PricingAnalyticEvent.INLINE_EDIT,
                  {
                    pageSource,
                    quickAction: PriceEditAction.SetLastPrice,
                    restaurantUUID,
                    supplierUUID: originalRow.supplierUUID,
                    variantPackUUID,
                  },
                );
              },
              valueLabel:
                lastPriceInMicros !== null
                  ? formatCurrency(lastPriceInMicros, currencyCode)
                  : EMPTY_VALUE_LABEL,
            },
          ]
        : []),
      ...(bookmarkAction ? [bookmarkAction] : []),
      revertAction,
    ];
  }, [
    applyComputedEdit,
    bookmarkAction,
    currencyCode,
    hasChanges,
    inputType,
    originalFormattedValue,
    originalRow,
    pageSource,
    restaurantUUID,
    revertEdits,
    scope,
    updatedRow,
    variantPackUUID,
  ]);

  const validate = useMemo(
    () =>
      buildPricingInputValidator({
        costInMicros: originalRow.costInMicros,
        currencyCode,
        enabled: priceFloorEnabled && Boolean(priceFloorMode),
        inputType,
      }),
    [
      currencyCode,
      inputType,
      originalRow.costInMicros,
      priceFloorEnabled,
      priceFloorMode,
    ],
  );

  const popoverProps = useMemo<PricingTableInputPopoverConfig>(
    () => ({
      currencyCode,
      historyContext: {
        enabled: isHistoricalPricingContextEnabled,
        field: inputType,
        history: originalRow.invoiceHistory,
      },
      isAnchorSettled,
      kind: PricingTableInputPopoverKind.QuickActionsWithHistory,
    }),
    [
      currencyCode,
      inputType,
      isAnchorSettled,
      isHistoricalPricingContextEnabled,
      originalRow.invoiceHistory,
    ],
  );

  const pricingTableInput = (
    <PricingTableInput
      bookmarked={isBookmarked}
      bookmarkNote={bookmarkNote}
      decimalPlaces={
        inputType === PricingEditField.GrossMarginPercentage ? 1 : 2
      }
      disabled={disabled}
      inputID={inputID}
      onArrowNavigation={onArrowNavigation}
      onBlur={onBlur}
      onFocus={reportCellFocus}
      onFocusChange={setIsFocused}
      onQuickActionsOpenChange={setQuickActionsOpen}
      originalValue={inputValue}
      popoverProps={popoverProps}
      quickActions={quickActions}
      quickActionsOpen={isQuickActionsOpen}
      updatedValue={updatedValue}
      validate={validate}
    />
  );

  return (
    <View collapsable={false} style={styles.container}>
      {isNoteOpen ? (
        <BookmarkNotePopover
          anchor={pricingTableInput}
          initialNote={bookmarkNote}
          onSave={onSaveNote}
        />
      ) : (
        pricingTableInput
      )}
      {updatedValue ? (
        <View style={styles.originalValue}>
          <Typography
            color={theme.sk.color.text.tertiary}
            testID="original-pricing-formatted-value"
            variant="paragraph-sm"
          >
            {originalFormattedValue}
          </Typography>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-end',
  },
  originalValue: {
    alignItems: 'flex-end',
    paddingRight: theme.sk.spacing.spaceSMedium + 1,
  },
}));
